import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { http } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await http.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    http.post("/auth/session", { session_id: sid })
      .then(async ({ data }) => {
        setUser(data);
        const { data: books } = await http.get("/books");
        navigate(books.length ? "/dashboard" : "/books/new", { replace: true });
      })
      .catch(() => navigate("/", { replace: true }));
  }, [location.hash, navigate, setUser]);

  return <div className="min-h-screen" data-testid="auth-callback" />;
}

export const PageLoading = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loading">
    <span className="label-mono caret">Setting the type</span>
  </div>
);

export function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/" replace />;
  if (admin && !user.is_admin) {
    return (
      <div className="max-w-xl mx-auto py-32 px-6 text-center" data-testid="admin-forbidden">
        <p className="label-mono mb-3">403 / Owner only</p>
        <h1 className="font-display text-4xl">This room is for the press owner.</h1>
      </div>
    );
  }
  return children;
}
